import { http } from "@/shared/api/axios"
import type { Site, CreateSiteRequest, UpdateSiteRequest, LookupSiteResponse } from "../model/types"

/**
 * Fetch sites for a specific client
 */
export const fetchClientSites = async (clientId: number): Promise<Site[]> => {
  const response = await http.get<Site[]>(`/v1/clients/${clientId}/sites`)
  return response.data
}

/**
 * Get site by ID
 */
export const fetchSiteById = async (siteId: number): Promise<Site> => {
  const response = await http.get<Site>(`/v1/sites/${siteId}`)
  return response.data
}

/**
 * Create a new site for a client
 */
export const createSite = async (
  clientId: number,
  request: CreateSiteRequest
): Promise<Site> => {
  const response = await http.post<Site>(`/v1/clients/${clientId}/sites`, request)
  return response.data
}

/**
 * Update an existing site
 */
export const updateSite = async (
  siteId: number,
  request: UpdateSiteRequest
): Promise<Site> => {
  const response = await http.patch<Site>(`/v1/sites/${siteId}`, request)
  return response.data
}

/**
 * Delete a site
 */
export const deleteSite = async (siteId: number): Promise<void> => {
  await http.delete(`/v1/sites/${siteId}`)
}

/**
 * Bring a parked site back into service. 204 whether or not it was already active; 402 when the plan's
 * active-site limit is reached.
 */
export const activateSite = async (siteId: number): Promise<void> => {
  await http.post(`/v1/sites/${siteId}/activate`)
}

/**
 * Park an active site: it keeps its history but accepts no new work. 204 on success; 409 while the site
 * still has shifts that have not ended (one in progress counts); 404 when the caller cannot see it. The
 * default error toast is skipped because its wording for a 409 ("Something went wrong") would hide the
 * reason; `useDeactivateSite` explains each outcome instead.
 */
export const deactivateSite = async (siteId: number): Promise<void> => {
  await http.post(`/v1/sites/${siteId}/deactivate`, undefined, { _skipErrorToast: true } as never)
}

export const fetchSiteLookup = async (clientId?: number, ids?: number[]): Promise<LookupSiteResponse[]> => {
  const response = await http.get<LookupSiteResponse[]>('/v1/lookup/client/sites', {
    params: {
      ...(clientId ? { clientId } : {}),
      ...(ids && ids.length > 0 ? { ids: ids.join(',') } : {}),
    },
  })
  return response.data
}
